import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, TextField, Button, Container, Typography, Box, List, ListItem, CircularProgress, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { connectSocket, disconnectSocket, sendMessage, isOnboardingActive } from '../services/socketService';
import { getIdentity } from '../services/identityService';
import { getProfile, getWelcomeOverlay, submitWelcomeOverlay, updateProfile } from '../services/authApiService';
import { useAuth } from '../contexts/AuthContext';
import MessageFormatter from './MessageFormatter';
import ProgressIndicator from './ProgressIndicator';
import PriorityGradingBar from './PriorityGradingBar';
import WelcomeOverlay from './WelcomeOverlay';
import LoadingIndicator from './LoadingIndicator';

// Status Light Configuration
const STATUS_LIGHT_CONFIG = {
  colors: {
    error: '#ff4444',
    active: '#00ff66', 
    idle: '#ffaa00'
  },
  animations: {
    pulseOpacityLow: 0.7,
    pulseOpacityHigh: 1.0,
    blinkOpacityLow: 0.3,
    blinkOpacityHigh: 1.0,
    flashScaleMin: 1.0,
    flashScaleMax: 1.1,
    flashOpacityMin: 0.5,
    flashOpacityMax: 1.0
  },
  timing: {
    pulseDuration: '3s',
    blinkDuration: '1.5s', 
    flashDuration: '0.5s',
    resetDelay: 1500 // ms
  }
};

const Interview = () => {
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [input, setInput] = useState('');
  const [profile, setProfile] = useState(null);
  const [showBrainLog, setShowBrainLog] = useState(false);
  const [logStatus, setLogStatus] = useState('idle'); // idle, running, error
  const [interviewTerminated, setInterviewTerminated] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [progressData, setProgressData] = useState({
    completed: 0,
    total: 0,
    currentQuestion: 0,
    loading: true
  });
  const [welcomeData, setWelcomeData] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeLoading, setWelcomeLoading] = useState(true);
  const [connectionEnabled, setConnectionEnabled] = useState(false);  // Wait for profile save before WebSocket

  // Priority Grading Bar state
  const [showPriorityGrading, setShowPriorityGrading] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [priorityFeedback, setPriorityFeedback] = useState('');

  // Answer editing state
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);  // Track current question_id
  const isSavingEditRef = useRef(false);  // Ref for synchronous access in event handlers

  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const systemLogsEndRef = useRef(null);
  const inputRef = useRef(null);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  const scrollSystemLogsToBottom = () => {
    systemLogsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages]);

  useEffect(() => {
    scrollSystemLogsToBottom()
  }, [systemLogs]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
        const fetchedProfile = await getProfile();
          setProfile(fetchedProfile);
        } catch (error) {
          console.error("Failed to fetch profile", error);
        }
      }
    };

    fetchProfile();
  }, [user]);


  // Load welcome overlay ONLY after login (not on navigation)
  useEffect(() => {
    if (!user) {
      setWelcomeData(null);
      setWelcomeOpen(false);
      setWelcomeLoading(false);
      return;
    }

    // Check if this is right after login (flag set by auth redirect)
    const justLoggedIn = sessionStorage.getItem('just_logged_in');
    console.log('[INTERVIEW] Checking login flag:', justLoggedIn);

    if (!justLoggedIn) {
      // This is navigation, not login - skip welcome, enable connection immediately
      console.log('[INTERVIEW] No login flag, skipping welcome');
      setWelcomeLoading(false);
      setConnectionEnabled(true);
      return;
    }

    console.log('[INTERVIEW] Login flag found! Loading welcome overlay');

    // Clear the flag immediately (so navigation won't show welcome)
    sessionStorage.removeItem('just_logged_in');

    let cancelled = false;

    const loadWelcomeOverlay = async () => {
      setWelcomeLoading(true);
      try {
        const overlay = await getWelcomeOverlay();
        if (cancelled) return;

        // Check for is_new_org flag from OAuth sync
        const isNewOrg = sessionStorage.getItem('is_new_org') === 'true';
        // Clear the flag after reading
        sessionStorage.removeItem('is_new_org');

        // Add is_new_org to overlay data
        const overlayWithOrgFlag = { ...overlay, is_new_org: isNewOrg };

        setWelcomeData(overlayWithOrgFlag);
        setWelcomeOpen(true);
      } catch (overlayError) {
        console.error('Failed to load welcome overlay', overlayError);
        if (!cancelled) {
          setWelcomeData(null);
          setWelcomeOpen(false);
        }
      } finally {
        if (!cancelled) {
          setWelcomeLoading(false);
        }
      }
    };

    loadWelcomeOverlay();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Parse brain log messages for progress updates
  const updateProgressFromBrainLog = (message) => {
    if (!message.content) return;

    const content = message.content;

    // Extract total questions from bootstrap: "Selected 12 questions"
    const totalMatch = content.match(/Selected (\d+) questions/);
    if (totalMatch) {
      const total = parseInt(totalMatch[1], 10);
      setProgressData(prev => ({ ...prev, total: total * 2, loading: false })); // 2 actions per question
      return;
    }

    // Extract current question from: "Question 8 asked:" and show question start
    const questionAskedMatch = content.match(/Question (\d+) asked:/);
    if (questionAskedMatch) {
      const currentQuestion = parseInt(questionAskedMatch[1], 10);
      setProgressData(prev => ({
        ...prev,
        currentQuestion,
        completed: (currentQuestion - 1) * 2 // Each question start = previous questions completed (2 actions each)
      }));
      return;
    }

    // Extract answer received: "Answer received for question 8:"
    const answerReceivedMatch = content.match(/Answer received for question (\d+):/);
    if (answerReceivedMatch) {
      const currentQuestion = parseInt(answerReceivedMatch[1], 10);
      setProgressData(prev => ({
        ...prev,
        completed: (currentQuestion - 1) * 2 + 1 // Previous questions + 1st action of current
      }));
      return;
    }

    // Extract importance received: "Importance received for question 8:"
    const importanceReceivedMatch = content.match(/Importance received for question (\d+):/);
    if (importanceReceivedMatch) {
      const currentQuestion = parseInt(importanceReceivedMatch[1], 10);
      setProgressData(prev => ({
        ...prev,
        completed: currentQuestion * 2 // Complete both actions for this question
      }));
      return;
    }
  };

  // WebSocket connection - waits for profile save via connectionEnabled
  useEffect(() => {
    if (!user?.email) {
      return;
    }

    // Wait for welcome overlay to complete (profile saved)
    if (!connectionEnabled) {
      console.log('[INTERVIEW] Waiting for welcome overlay to complete before connecting');
      return;
    }

    const handleNewMessage = (message) => {
      // Handle edit responses first (don't add to messages or system logs)
      if (message.type === 'edit_success') {
        // Edit was successful - update the message in UI with content from backend
        setMessages((prevMessages) => {
          const updated = [...prevMessages];
          // Match by question_id, type='user', AND content_type to find the correct message
          const index = updated.findIndex(msg =>
            msg.question_id === message.questionId &&
            msg.type === 'user' &&
            msg.content_type === message.contentType
          );
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              content: message.newContent,  // Use content from backend response
              edited: true
            };
          }
          return updated;
        });
        // Exit edit mode and ensure typing indicator is OFF
        setEditingMessageIndex(null);
        setEditingContent('');
        setIsSavingEdit(false);
        isSavingEditRef.current = false;  // Clear ref synchronously
        setIsAiTyping(false);  // Explicitly turn off typing indicator after edit

        return; // Don't process further
      } else if (message.type === 'edit_error') {
        // Edit failed - show error and exit edit mode
        alert(`Failed to save edit: ${message.error}`);
        setEditingMessageIndex(null);
        setEditingContent('');
        setIsSavingEdit(false);
        isSavingEditRef.current = false;  // Clear ref synchronously
        setIsAiTyping(false);  // Explicitly turn off typing indicator after edit error

        return; // Don't process further
      } else if (message.type === 'progress_restore') {
        // Restore progress bar state when reconnecting to existing session
        setProgressData({
          total: message.total,
          currentQuestion: message.currentQuestion,
          completed: message.completed,
          loading: false
        });
        console.log(`Progress restored: ${message.completed}/${message.total}`);
        return; // Don't process further
      }

      const systemMessageTypes = [
        'system',
        'response_completed',
        'agent_reasoning',
        'agent_transition',
        'processing_update',
        'brain_log',
        'state_change',
        'performance_metric',
        'error_recovery',
        'error'
      ];

      const isSystemMessage = systemMessageTypes.includes(message.type);

      if (isSystemMessage) {
        const timestampedLog = { ...message, receivedAt: new Date() };
        setSystemLogs((prevLogs) => [...prevLogs, timestampedLog]);
        updateProgressFromBrainLog(message);
        setLogStatus(message.type === 'error' ? 'error' : 'running');

        if (message.type === 'brain_log' || message.type === 'agent_reasoning' || message.type === 'agent_transition') {
          // Don't show typing indicator during edit operations (use ref for synchronous check)
          if (!isSavingEditRef.current) {
            setIsAiTyping(true);
          }
        }

        setTimeout(() => {
          setLogStatus(message.type === 'error' ? 'error' : 'idle');
        }, STATUS_LIGHT_CONFIG.timing.resetDelay);
      } else {
        setIsAiTyping(false);
        setMessages((prevMessages) => [...prevMessages, message]);

        // Track question_id when receiving a question for answer editing
        if (message.type === 'question' && message.question_id) {
          setCurrentQuestionId(message.question_id);
        }

        if (message.type === 'importance_request' && message.show_priority_grading === true) {
          setShowPriorityGrading(true);
          setSelectedPriority(null);
          setPriorityFeedback('');
        } else {
          setShowPriorityGrading(false);
        }
      }

      if (message.type === 'question' || message.type === 'processing_complete' || message.type === 'onboarding_message') {
        setIsAiTyping(false);
      }

      if (message.type === 'interview_complete') {
        const completionMessage = {
          type: 'completion',
          text: `Thank you for completing the AI Assessment! Your responses have been analyzed and your personalized report is ready.`,
          sessionData: message.data,
          timestamp: new Date().toISOString()
        };
        setMessages((prevMessages) => [...prevMessages, completionMessage]);
      }

      if (message.type === 'interview_terminated') {
        const terminationMessage = {
          type: 'termination',
          text: message.message || `We're experiencing technical difficulties. Please try again later.`,
          timestamp: new Date().toISOString()
        };
        setMessages((prevMessages) => [...prevMessages, terminationMessage]);
        setInterviewTerminated(true);
      }

      if (message.type === 'session_timeout') {
        const timeoutMessage = {
          type: 'timeout',
          text: message.message || 'Your session has timed out due to inactivity or technical updates. Your progress has been saved. Redirecting to login...',
          timestamp: new Date().toISOString()
        };
        setMessages((prevMessages) => [...prevMessages, timeoutMessage]);
        setInterviewTerminated(true);

        // Trigger logout after delay to show message
        setTimeout(() => {
          logout();
        }, 3000); // 3 second delay to show timeout message
      }
    };

    const connectTimer = setTimeout(async () => {
      try {
        const identity = await getIdentity();
        connectSocket(identity.user_id, handleNewMessage);

        // Bootstrap happens automatically on backend - no signal needed

      } catch (e) {
        console.error('Failed to resolve identity for WebSocket connection', e);
      }
    }, 100);

    return () => {
      clearTimeout(connectTimer);
      if (!isOnboardingActive()) {
        disconnectSocket();
      }
    };
  }, [user, navigate, connectionEnabled]);


  const handleSendMessage = async () => {
    if (welcomeOpen) {
      return;
    }

    // Handle priority grading submission
    if (showPriorityGrading && selectedPriority !== null) {
      // Combine number and optional feedback text
      const priorityContent = selectedPriority + (priorityFeedback.trim() ? ': ' + priorityFeedback.trim() : '');

      sendMessage({
        type: 'user_response',
        content: priorityContent,
        question_id: currentQuestionId,
        priority_data: {
          number: selectedPriority,
          text: priorityFeedback.trim()
        }
      });

      setMessages((prevMessages) => [...prevMessages, {
        type: 'user',
        content: priorityContent,
        question_id: currentQuestionId,
        content_type: 'importance'  // Mark as importance rating
      }]);

      // Reset priority grading state
      setShowPriorityGrading(false);
      setSelectedPriority(null);
      setPriorityFeedback('');

      // Show typing indicator
      setIsAiTyping(true);

      // Auto-focus back to regular input field after priority submission
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    // Handle regular text input submission
    else if (input.trim()) {
      sendMessage({ type: 'user_response', content: input, question_id: currentQuestionId });
      setMessages((prevMessages) => [...prevMessages, {
        type: 'user',
        content: input,
        question_id: currentQuestionId,
        content_type: 'answer'  // Mark as answer
      }]);
      setInput('');
      // Show typing indicator immediately when user sends message
      setIsAiTyping(true);

      // Auto-focus back to input field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  // Answer editing handlers
  const startEdit = (index) => {
    const message = messages[index];
    setEditingMessageIndex(index);
    setEditingContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (index) => {
    if (!editingContent.trim()) return;

    setIsSavingEdit(true);
    isSavingEditRef.current = true;  // Set ref synchronously

    try {
      // Ensure typing indicator is OFF during edit (we show spinner instead)
      setIsAiTyping(false);

      // Send edit message to backend with real question_id and content type
      sendMessage({
        type: 'edit_answer',
        questionId: messages[index].question_id,
        newAnswer: editingContent,
        originalAnswer: messages[index].content,
        contentType: messages[index].content_type || 'answer'  // 'answer' or 'importance'
      });

      // NOTE: Do NOT update UI here - wait for edit_success response from backend
      // The UI will be updated in handleNewMessage when edit_success is received
      // Keep edit mode active until we get response

    } catch (error) {
      console.error('Failed to save edit:', error);
      alert('Failed to save changes. Please try again.');
      setIsSavingEdit(false);
      isSavingEditRef.current = false;  // Clear ref synchronously
    }
  };

  const handleGoToResults = () => {
    setWelcomeOpen(false);
    navigate('/results');
  };

  const handleWelcomeSubmit = async (payload) => {
    try {
      await submitWelcomeOverlay(payload);

      // Update profile with submitted data
      setProfile((prevProfile) => {
        if (!prevProfile) return prevProfile;
        return {
          ...prevProfile,
          title: payload.title,
          responsibilities: payload.responsibilities,
          department: payload.department,
          language: payload.language
        };
      });

      // Close welcome overlay and enable WebSocket connection
      setWelcomeOpen(false);
      setConnectionEnabled(true);

    } catch (overlayError) {
      console.error('Failed to submit welcome overlay', overlayError);
      alert('Saving failed. Please try again.');
    }
  };

  const handleWelcomeLanguageChange = async (nextLanguage) => {
    if (!nextLanguage) {
      return;
    }
    try {
      await updateProfile({ language: nextLanguage });
      setProfile((prev) => (prev ? { ...prev, language: nextLanguage } : prev));
      setWelcomeData((prev) =>
        prev
          ? {
              ...prev,
              language: nextLanguage
            }
          : prev
      );
    } catch (error) {
      console.error('Failed to update language preference', error);
      alert('We could not update your language preference. Please try again.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent the Enter from creating a new line
      handleSendMessage();
    }
  };

  // Show loading overlay until we determine if welcome should be shown
  if (welcomeLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(245, 245, 245, 0.7)',
          backdropFilter: 'blur(3px)',
          zIndex: 2000
        }}
      >
        <LoadingIndicator />
      </Box>
    );
  }

  return (
    <>
      <WelcomeOverlay
        open={welcomeOpen}
        loading={false}
        data={welcomeData}
        onSubmit={handleWelcomeSubmit}
        onLanguageSelect={handleWelcomeLanguageChange}
        onGoToResults={handleGoToResults}
      />
      <Container maxWidth="xl" sx={{
        px: { xs: 1, sm: 2 },
        backgroundColor: '#f5f5f7',
        minHeight: '100vh',
        py: { xs: 1, sm: 2 }
      }}>
      <Box sx={{ py: { xs: 0.5, sm: 1 } }}>


        {/* Main Chat Area */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 1
          }}>
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontFamily: 'Bebas Neue', 
                fontWeight: 'normal', 
                color: '#4b0000',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                mb: 0
              }}
            >
              AI ASSESSMENT CHAT
            </Typography>
            
            {/* User Info - Same level as header, hidden on narrow screens */}
            {profile && (
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center'
              }}>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  color: '#666',
                  mr: 1
                }}>
                  Logged in as: {profile.name} ({user?.email})
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  onClick={() => navigate('/profile')}
                  sx={{ 
                    color: '#4b0000',
                    minWidth: 'auto',
                    p: 0.5
                  }}
                >
                  <EditIcon fontSize="small" />
                </Button>
              </Box>
            )}
          </Box>
          <Paper
            elevation={0}
            sx={{
              height: showBrainLog
                ? { xs: 'calc(75vh - 320px)', md: 'calc(85vh - 320px)' }
                : { xs: 'calc(75vh - 120px)', md: 'calc(85vh - 120px)' },
              minHeight: '300px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'height 0.18s ease-out',
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Chat Messages Area */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
              {/* Post-Welcome Overlay Loading Indicator */}
              {!welcomeOpen && connectionEnabled && messages.length === 0 && !isAiTyping && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%'
                }}>
                  <LoadingIndicator />
                </Box>
              )}
              <List>
                {messages.map((msg, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        display: 'flex',
                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                        alignItems: 'flex-start',
                        px: { xs: 0, sm: 1 },
                        py: 1.5
                      }}
                    >
                      {msg.type === 'completion' ? (
                        <Box sx={{ 
                          width: '100%', 
                          textAlign: 'center', 
                          p: 2,
                          animation: 'messageBubbleAppear 0.25s ease-out',
                          '@keyframes messageBubbleAppear': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(15px) scale(0.96)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0px) scale(1)'
                            }
                          }
                        }}>
                          <Typography variant="h6" sx={{ color: '#4b0000', fontFamily: 'Bebas Neue', mb: 2 }}>
                            🎉 INTERVIEW COMPLETED! 🎉
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                            {msg.text}
                          </Typography>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/results')}
                            sx={{
                              backgroundColor: '#e04111',
                              '&:hover': { backgroundColor: '#4b0000' },
                              minWidth: '200px',
                              borderRadius: '25px',
                              fontSize: '1.1rem',
                              fontWeight: 'bold'
                            }}
                          >
                            VIEW YOUR RESULTS
                          </Button>
                        </Box>
                      ) : msg.type === 'termination' ? (
                        <Box sx={{ 
                          width: '100%', 
                          textAlign: 'center', 
                          p: 2,
                          animation: 'messageBubbleAppear 0.25s ease-out',
                          '@keyframes messageBubbleAppear': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(15px) scale(0.96)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0px) scale(1)'
                            }
                          }
                        }}>
                          <Typography variant="h6" sx={{ color: '#d32f2f', fontFamily: 'Bebas Neue', mb: 2 }}>
                            INTERVIEW PAUSED
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                            {msg.text}
                          </Typography>
                        </Box>
                      ) : msg.type === 'timeout' ? (
                        <Box sx={{ 
                          width: '100%', 
                          textAlign: 'center', 
                          p: 2,
                          animation: 'messageBubbleAppear 0.25s ease-out',
                          '@keyframes messageBubbleAppear': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(15px) scale(0.96)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0px) scale(1)'
                            }
                          }
                        }}>
                          <Typography variant="h6" sx={{ color: '#ff9800', fontFamily: 'Bebas Neue', mb: 2 }}>
                            ⏰ SESSION TIMEOUT
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                            {msg.text}
                          </Typography>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => window.location.reload()}
                            sx={{
                              backgroundColor: 'rgba(224, 65, 17, 0.9)',
                              '&:hover': { backgroundColor: 'rgba(224, 65, 17, 1)' },
                              minWidth: '200px',
                              borderRadius: '8px',
                              fontSize: '1.1rem',
                              fontWeight: 'bold'
                            }}
                          >
                            RESTART INTERVIEW
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{
                          backgroundColor: msg.type === 'user'
                            ? 'rgba(224, 65, 17, 0.04)'
                            : 'rgba(2, 28, 45, 0.03)',
                          borderRadius: '12px',
                          p: { xs: 2.5, sm: 2.5 },
                          maxWidth: msg.type === 'user'
                            ? { xs: '75%', sm: '65%', md: '55%' }
                            : { xs: '90%', sm: '85%', md: '75%' },
                          border: msg.type === 'user'
                            ? '1px solid rgba(224, 65, 17, 0.12)'
                            : '1px solid rgba(2, 28, 45, 0.12)',
                          boxShadow: msg.type === 'user'
                            ? '0 1px 3px rgba(0, 0, 0, 0.04)'
                            : '0 0 24px rgba(2, 28, 45, 0.12)',
                          wordWrap: 'break-word',
                          animation: 'messageBubbleAppear 0.18s ease-out',
                          transition: 'all 0.18s ease-out',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            backgroundColor: msg.type === 'user'
                              ? 'rgba(224, 65, 17, 0.06)'
                              : 'rgba(2, 28, 45, 0.05)',
                            boxShadow: msg.type === 'user'
                              ? '0 2px 6px rgba(0, 0, 0, 0.06)'
                              : '0 0 32px rgba(2, 28, 45, 0.16)',
                            '& .edit-icon': {
                              opacity: 1
                            }
                          },
                          '@keyframes messageBubbleAppear': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(15px) scale(0.96)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0px) scale(1)'
                            }
                          }
                        }}>
                          <Box sx={{
                            fontFamily: 'Inter, sans-serif',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                          }}>
                            {editingMessageIndex === index ? (
                              // Edit mode: Show TextField with Save/Cancel buttons
                              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  value={editingContent}
                                  onChange={(e) => setEditingContent(e.target.value)}
                                  disabled={isSavingEdit}
                                  autoFocus
                                  sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                      fontFamily: 'Inter, sans-serif',
                                      fontSize: '1rem',
                                      width: '100%'
                                    },
                                    '& .MuiInputBase-input': {
                                      width: '100%'
                                    }
                                  }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    disabled={isSavingEdit}
                                    sx={{ color: 'rgba(0, 0, 0, 0.54)' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveEdit(index)}
                                    disabled={isSavingEdit || !editingContent.trim()}
                                    sx={{ color: 'rgba(224, 65, 17, 0.7)' }}
                                  >
                                    {isSavingEdit ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <CheckIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              // View mode: Show message with edit icon
                              <>
                                <Box sx={{ flex: 1 }}>
                                  <MessageFormatter content={msg.content || msg.message} />
                                  {msg.edited && (
                                    <Typography variant="caption" sx={{ opacity: 0.6, fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                                      (edited)
                                    </Typography>
                                  )}
                                </Box>
                                {msg.type === 'user' && (
                                  <IconButton
                                    size="small"
                                    onClick={() => startEdit(index)}
                                    className="edit-icon"
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      p: 0.5
                                    }}
                                  >
                                    <EditIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                  </IconButton>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </ListItem>
                  </React.Fragment>
                ))}
                {/* Typing Indicator */}
                {isAiTyping && (
                  <ListItem sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    px: { xs: 0, sm: 1 },
                    py: 1.5
                  }}>
                    <Box sx={{
                      backgroundColor: 'rgba(2, 28, 45, 0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(2, 28, 45, 0.12)',
                      boxShadow: '0 0 24px rgba(2, 28, 45, 0.12)',
                      p: { xs: 1.5, sm: 1.5 },
                      width: '60px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'messageBubbleAppear 0.25s ease-out',
                      '@keyframes messageBubbleAppear': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(15px) scale(0.96)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0px) scale(1)'
                        }
                      }
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 0.5,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        '@keyframes typingDots': {
                          '0%, 60%, 100%': { transform: 'translateY(0px)', opacity: 0.4 },
                          '30%': { transform: 'translateY(-10px)', opacity: 1 }
                        }
                      }}>
                        <Box sx={{ 
                          width: '6px', 
                          height: '6px', 
                          backgroundColor: '#666', 
                          borderRadius: '50%',
                          animation: 'typingDots 1.4s infinite ease-in-out',
                          animationDelay: '0.0s'
                        }} />
                        <Box sx={{ 
                          width: '6px', 
                          height: '6px', 
                          backgroundColor: '#666', 
                          borderRadius: '50%',
                          animation: 'typingDots 1.4s infinite ease-in-out',
                          animationDelay: '0.2s'
                        }} />
                        <Box sx={{ 
                          width: '6px', 
                          height: '6px', 
                          backgroundColor: '#666', 
                          borderRadius: '50%',
                          animation: 'typingDots 1.4s infinite ease-in-out',
                          animationDelay: '0.4s'
                        }} />
                      </Box>
                    </Box>
                  </ListItem>
                )}
                <div ref={messagesEndRef} />
              </List>
            </Box>
            
            {/* Integrated Input Area */}
            <Box sx={{
              borderTop: '1px solid #e0e0e0',
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#fafafa',
              gap: { xs: 1, sm: 0 }
            }}>
              {/* Conditionally show PriorityGradingBar or TextField */}
              {showPriorityGrading ? (
                <PriorityGradingBar
                  selectedNumber={selectedPriority}
                  onNumberSelect={setSelectedPriority}
                  feedbackText={priorityFeedback}
                  onFeedbackChange={setPriorityFeedback}
                  onSubmit={handleSendMessage}
                  disabled={interviewTerminated || welcomeOpen}
                />
              ) : (
                <TextField
                  inputRef={inputRef}
                  placeholder={interviewTerminated ? "Interview paused" : "Type your answer here... (Shift+Enter for new line)"}
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  value={input}
                  disabled={interviewTerminated || welcomeOpen}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      backgroundColor: 'white',
                      minHeight: { xs: '44px', sm: 'auto' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4b0000',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              )}
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={welcomeOpen || interviewTerminated || (showPriorityGrading ? selectedPriority === null : !input.trim())}
                sx={{
                  ml: { xs: 0, sm: 1 },
                  borderRadius: '8px',
                  minWidth: '60px',
                  backgroundColor: '#ffffff', 
                  color: '#011c2d',
                  border: '2px solid #011c2d',
                  '&:hover': { 
                    backgroundColor: '#011c2d !important',
                    color: '#ffffff !important',
                    border: '2px solid #ffffff !important'
                  },
                  '&:active': {
                    backgroundColor: '#011c2d !important',
                    color: '#ffffff !important'
                  },
                  '&:disabled': { 
                    backgroundColor: '#ccc !important',
                    color: '#666 !important',
                    border: '2px solid #999 !important'
                  },
                  minHeight: { xs: '44px', sm: 'auto' },
                  px: { xs: 2, sm: 3 },
                  transition: 'all 0.2s ease'
                }}
              >
                Send
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Console-style Brain Log at Bottom */}
        <Box sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Button
              onClick={() => setShowBrainLog(!showBrainLog)}
              variant="text"
              size="small"
              sx={{ 
                color: '#666',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                p: 0
              }}
            >
            {showBrainLog ? '▼' : '▶'} AI AGENTS ACTIVITY ({systemLogs.length})
            <Box 
              sx={{
                width: '6px',
                height: '6px', 
                borderRadius: '50%',
                ml: 1,
                backgroundColor: 
                  logStatus === 'error' ? STATUS_LIGHT_CONFIG.colors.error : 
                  logStatus === 'running' ? STATUS_LIGHT_CONFIG.colors.active :
                  STATUS_LIGHT_CONFIG.colors.idle,
                animation: 
                  logStatus === 'error' ? `blinkLight ${STATUS_LIGHT_CONFIG.timing.blinkDuration} infinite` :
                  logStatus === 'running' ? `flashLight ${STATUS_LIGHT_CONFIG.timing.flashDuration} ease-in-out` :
                  `pulseLight ${STATUS_LIGHT_CONFIG.timing.pulseDuration} infinite`,
                '@keyframes blinkLight': {
                  '0%, 50%': { opacity: STATUS_LIGHT_CONFIG.animations.blinkOpacityHigh },
                  '51%, 100%': { opacity: STATUS_LIGHT_CONFIG.animations.blinkOpacityLow }
                },
                '@keyframes flashLight': {
                  '0%': { 
                    opacity: STATUS_LIGHT_CONFIG.animations.flashOpacityMin, 
                    transform: `scale(${STATUS_LIGHT_CONFIG.animations.flashScaleMin})` 
                  },
                  '50%': { 
                    opacity: STATUS_LIGHT_CONFIG.animations.flashOpacityMax, 
                    transform: `scale(${STATUS_LIGHT_CONFIG.animations.flashScaleMax})` 
                  },
                  '100%': { 
                    opacity: STATUS_LIGHT_CONFIG.animations.pulseOpacityHigh, 
                    transform: `scale(${STATUS_LIGHT_CONFIG.animations.flashScaleMin})` 
                  }
                },
                '@keyframes pulseLight': {
                  '0%, 100%': { opacity: STATUS_LIGHT_CONFIG.animations.pulseOpacityLow },
                  '50%': { opacity: STATUS_LIGHT_CONFIG.animations.pulseOpacityHigh }
                }
              }}
            />
          </Button>
          
          {/* Progress Indicator - Right aligned */}
          <ProgressIndicator 
            completed={progressData.completed}
            total={progressData.total}
            loading={progressData.loading}
          />
        </Box>
          
          {showBrainLog && (
            <Paper
              elevation={0}
              sx={{
                height: '200px',
                overflowY: 'auto',
                backgroundColor: '#1e1e1e',
                color: '#00ff66',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                p: 1,
                border: '1px solid #333'
              }}
            >
              <List dense sx={{ py: 0 }}>
                {systemLogs.map((log, index) => (
                  <ListItem key={index} sx={{ py: 0.2, px: 0.5 }}>
                    <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                      <Typography 
                        sx={{ 
                          fontSize: '0.7rem', 
                          color: '#858585',
                          minWidth: '60px',
                          fontFamily: 'monospace'
                        }}
                      >
                        {log.receivedAt 
                          ? log.receivedAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'}) 
                          : ''}
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontSize: '0.75rem', 
                          color: '#00ff66',
                          fontFamily: 'monospace',
                          flex: 1,
                          lineHeight: 1.2,
                          textShadow: '0 0 5px rgba(0,255,102,0.7)'
                        }}
                      >
                        {log.content || log.message || "Orchestrator Agent: Continuing with next task..."}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
                <div ref={systemLogsEndRef} />
              </List>
            </Paper>
          )}
        </Box>
      </Box>
      </Container>
    </>
  );
};

export default Interview;
